<template>
  <div class="row mt-4">
    <div v-if="administratorDataIsLoading" class="d-flex justify-content-center m-5">
      <div class="spinner-border text-primary">
        <span class="sr-only">{{$t('state.loading')}}</span>
      </div>
    </div>
    <b-form v-if="!administratorDataIsLoading" class="w-100 d-flex">
      <div class="col-6">
        <b-form-group
            :label="$t('administrator.edit.email-input')"
            label-for="email"
        >
          <b-form-input
              id="email"
              type="email"
              v-model="email"
              @input="$v.email.$model = $event"
              :state="!$v.email.$dirty ? null :(!$v.email.$error)"
              trim></b-form-input>
          <p v-if="$v.email.$dirty && !$v.email.required" class="text-danger">
            {{ $t('administrator.errors.required') }}
          </p>
          <p v-if="$v.email.$dirty && !$v.email.email" class="text-danger">
            {{ $t('administrator.errors.email-incorrect') }}
          </p>
        </b-form-group>

        <b-form-group
            :label="$t('administrator.edit.phone-input')"
            label-for="phoneNumber"
        >
          <b-form-input
              id="phoneNumber"
              v-model="phoneNumber"
              @input="$v.phoneNumber.$model = $event"
              :state="!$v.phoneNumber.$dirty ? null :(!$v.phoneNumber.$error)"
              trim></b-form-input>
          <p v-if="$v.phoneNumber.$dirty && !$v.phoneNumber.required" class="text-danger">
            {{ $t('administrator.errors.required') }}
          </p>
        </b-form-group>

        <b-form-group
            :label="$t('administrator.edit.locale-input')"
            label-for="locale"
        >
          <b-form-select
              id="locale"
              v-model="locale"
              :options="localeOptions"
              @change="$v.locale.$model = $event"
              :state="!$v.locale.$dirty ? null :(!$v.locale.$error)"
              value-field="item"
              text-field="name"
              disabled-field="notEnabled"
          ></b-form-select>
          <p v-if="$v.locale.$dirty && !$v.locale.required" class="text-danger">
            {{ $t('administrator.errors.required') }}
          </p>
        </b-form-group>

        <b-form-group
            :label="$t('administrator.edit.tenantId')"
            label-for="tenantId"
        >
          <b-form-select
              id="tenantId"
              v-model="tenantId"
              :options="tenants"
              @change="$v.tenantId.$model = $event"
              :state="!$v.tenantId.$dirty ? null :(!$v.tenantId.$error)"
              value-field="id"
              text-field="name"
              :disabled="!isCreateAdministratorPage"
          ></b-form-select>
          <p v-if="$v.tenantId.$dirty && !$v.tenantId.required" class="text-danger">
            {{ $t('administrator.errors.required') }}
          </p>
        </b-form-group>

        <b-form-group
            :label="$t('administrator.edit.timezone-input')"
            label-for="timezone"
        >
          <b-form-select
              id="timezone"
              v-model="timezone"
              :options="timezoneOptions"
              @change="$v.timezone.$model = $event"
              :state="!$v.timezone.$dirty ? null :(!$v.timezone.$error)"
              value-field="item"
              text-field="name"
              disabled-field="notEnabled"
          ></b-form-select>
          <p v-if="$v.timezone.$dirty && !$v.timezone.required" class="text-danger">
            {{ $t('administrator.errors.required') }}
          </p>
        </b-form-group>

        <b-form-group
            :label="$t('administrator.edit.date-format-input')"
            label-for="date-format"
        >
          <b-form-select
              id="date-format"
              v-model="dateFormat"
              :options="dateFormatOptions"
              @change="$v.dateFormat.$model = $event"
              :state="!$v.dateFormat.$dirty ? null :(!$v.dateFormat.$error)"
              value-field="item"
              text-field="name"
              disabled-field="notEnabled"
          ></b-form-select>
          <p v-if="$v.dateFormat.$dirty && !$v.dateFormat.required" class="text-danger">
            {{ $t('administrator.errors.required') }}
          </p>
        </b-form-group>
        <div class="d-flex mt-4">
          <b-form-checkbox
              id="clock24Hours"
              v-model="clock24Hours"
              class="mr-5">
            {{ $t('administrator.labels.clock24') }}
          </b-form-checkbox>

          <b-form-group>
            <b-form-checkbox
                v-model="enabled"
                class="mr-5"
            >
              {{ $t('administrator.labels.enabled') }}
            </b-form-checkbox>
          </b-form-group>
          <b-form-group>
            <b-form-checkbox
                v-model="locked"
            >
              {{ $t('administrator.labels.locked') }}
            </b-form-checkbox>
          </b-form-group>
        </div>
        <div v-if="!isCreateAdministratorPage || administratorData" class="d-flex justify-content-end">
          <router-link :to="{ name: 'administrator' }">
            <button type="button" class="btn btn-secondary mt-4 pl-5 pr-5 mr-1">{{ $t('administrator.details.btn.cancel') }}</button>
          </router-link>
          <button type="button" @click="saveAdministratorData" class="btn btn-success mt-4 pl-5 pr-5">{{ $t('administrator.details.btn.save') }}</button>
        </div>
      </div>
      <div class="col-6">
        <AdministratorPasswordForm ref="administrator-password-form"></AdministratorPasswordForm>
      </div>
    </b-form>
  </div>
</template>

<script>
import { mapState } from "vuex";
import {required, email, helpers} from 'vuelidate/lib/validators';
import moment from "moment";
import alertify from "alertifyjs";
import AdministratorPasswordForm from "./AdministratorPasswordForm";

const phoneValidatorRegex = helpers.regex('passwordValidatorRegex', /^[+0-9 ]*$/);

export default {
  components: {
    AdministratorPasswordForm
  },
  data() {
    return {
      timezoneOptions: moment.tz.names(),
      dateFormatOptions: ['DD-MM-YYYY', 'MM-DD-YYYY', 'YYYY-MM-DD'],
    }
  },
  validations: {

    email: {
      required,
      email
    },
    phoneNumber: {
      required,
      phoneValidatorRegex
    },
    locale: {
      required
    },
    tenantId: {
      required
    },
    timezone: {
      required
    },
    dateFormat: {
      required
    }
  },
  created() {
    this.$store.dispatch('administrator/getTenants').then(() => {
      if(!this.isCreateAdministratorPage) {
        this.setInitialFormData();
      }
    });


  },
  mounted() {
    this.$v.$reset();
  },
  computed: {
    ...mapState({
      isCreateAdministratorPage: state => state.administrator.isCreateAdministratorPage,
      administratorData: state => state.administrator.administratorDetails,
      administratorDataForm: state => state.administrator.administratorDataForm,
      administratorPasswordForm: state => state.administrator.administratorPasswordForm,
      administratorDataIsLoading: state => state.administrator.administratorDataIsLoading,
      tenants: state => state.administrator.tenants
    }),
    localeOptions: function () {
      return this.$store.getters["root/locale"]
          .filter(o => o.status === 'PUBLISHED' && o.domainGroup === process.env.VUE_APP_ADMIN_GROUP_NAME)
          .map(l => this.$t(l.label));
    },
    email: {
      get() {
        return this.$store.state.administrator.administratorDataForm.email;
      },
      set(value) {
        this.$store.commit('administrator/setFormEmail', value)
      }
    },
    phoneNumber: {
      get() {
        return this.$store.state.administrator.administratorDataForm.phoneNumber;
      },
      set(value) {
        this.$store.commit('administrator/setFormPhoneNumber', value);
      }
    },
    clock24Hours: {
      get() {
        return this.$store.state.administrator.administratorDataForm.clock24Hours
      },
      set(value) {
        this.$store.commit('administrator/setFormClock24Hours', value);
      }
    },
    locale: {
      get() {
        return this.$store.state.administrator.administratorDataForm.locale
      },
      set(value) {
        this.$store.commit('administrator/setFormLocale', value);
      }
    },
    tenantId: {
      get() {
        return this.$store.state.administrator.administratorDataForm.tenantId
      },
      set(value) {
        this.$store.commit('administrator/setFormTenantId', value);
      }
    },
    timezone: {
      get() {
        return this.$store.state.administrator.administratorDataForm.timezone;
      },
      set(value) {
        this.$store.commit('administrator/setFormTimezone', value);
      }
    },
    dateFormat: {
      get() {
        return this.$store.state.administrator.administratorDataForm.dateFormat;
      },
      set(value) {
        this.$store.commit('administrator/setFormDateFormat', value);
      }
    },
    plainPassword: {
      get() {
        return this.$store.state.administrator.administratorDataForm.plainPassword;
      },
      set(value) {
        this.$store.commit('administrator/setFormPlainPassword', value)
      }
    },
    repeatedPassword: {
      get() {
        return this.$store.state.administrator.administratorDataForm.repeatedPassword;
      },
      set(value) {
        this.$store.commit('administrator/setFormRepeatedPassword', value)
      }
    },
    enabled: {
      get() {
        return this.$store.state.administrator.administratorDataForm.enabled;
      },
      set(value) {
        this.$store.commit('administrator/setFormEnabled', value)
      }
    },
    locked: {
      get() {
        return this.$store.state.administrator.administratorDataForm.locked;
      },
      set(value) {
        this.$store.commit('administrator/setFormLocked', value)
      }
    }
  },
  watch: {
    administratorData() {
      this.setInitialFormData();
    }
  },
  methods: {
    setInitialFormData() {
      this.$store.commit('administrator/resetAdministratorDataForm');
    },
    saveAdministratorData() {
      const passwordForm = this.isCreateAdministratorPage ? this.$refs['administrator-password-form'] : null;

      if((passwordForm && !passwordForm.$v.$invalid && !this.$v.$invalid) || (!passwordForm && !this.$v.$invalid)) {
        return new Promise((resolve, reject) => {
          const data = {
            email: this.administratorDataForm.email,
            phoneNumber: this.administratorDataForm.phoneNumber,
            enabled: this.administratorDataForm.enabled,
            locked: this.administratorDataForm.locked,
            tenantId: this.administratorDataForm.tenantId.uuid,
            profile: {
              locale: this.administratorDataForm.locale,
              timezone: this.administratorDataForm.timezone,
              dateFormat: this.administratorDataForm.dateFormat,
              clock24Hours: this.administratorDataForm.clock24Hours
            }
          }

          if(passwordForm) {
            data.plainPassword = this.administratorPasswordForm.plainPassword;
          }

          this.$store.dispatch('administrator/setAdministratorData', data).then(resp => {
            this.$store.dispatch('administrator/getAdministratorDetails', resp.data.id.uuid);
            this.$store.dispatch('root/fetchUser');
            resolve(resp);
          })
          .then(() => {
            this.$v.$reset();

            if(passwordForm) {
              passwordForm.$v.$reset();
            }

            this.$store.commit('administrator/setIsCreateAdministratorPage', false);

            alertify.success('Success');
          })
          .catch(error => {
            reject()
            alertify.error(error.response.data.title);
          });
        });
      } else {
        this.$v.$touch();

        if(passwordForm) {
          passwordForm.$v.$touch();
        }
      }
    }
  }
}
</script>
